import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import * as d3 from 'd3'

import ExportDropdown from '@aytm/app/stats/components/export-dropdown/ExportDropdown'
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'

import { calcMenuChartHeight } from '@aytm/app/stats/page/question-views/mn/components/menu-section/lib/constants'
import {
  DEFAULT_CONTAINER_WIDTH,
  EXPORT_OPTIONS,
  MENU_VIEW_MODES
} from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/lib/constants'
import {
  getHeatmapData,
  hasOverlayData
} from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/lib/overlayData'
import {
  OVERLAY_CLASS,
  clearOverlay,
  drawOverlayInto,
  overlayId,
  overlayOpacity,
  overlaySize
} from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/lib/render'
import styles from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/styles/MenuChart.module.scss'
import { MEDIA_INSET } from '@aytm/app/stats/page/question-views/components/lib/constants'

const MenuChart = ({ id, image, answer, sections, menuViewMode, isAnimated, onExport }) => {
  const wrapRef = useRef(null)
  const overlayRef = useRef(null)

  const [containerWidth, setContainerWidth] = useState(DEFAULT_CONTAINER_WIDTH)
  // pixels arrived (or failed) — the only thing the schema cannot tell upfront
  const [settled, setSettled] = useState(false)
  const [failed, setFailed] = useState(false)

  const imageUrl = image?.original?.url

  // the wrap exists from the first frame — one synchronous measure before paint
  useLayoutEffect(() => {
    const width = wrapRef.current?.clientWidth
    if (width) setContainerWidth(width)
  }, [])

  // schema dims make the display synchronous (D10) — only `settled` awaits the network
  const display = useMemo(() => {
    const { width, height } = image?.original || {}
    if (!width || !height) return null

    // scaled to the room inside the media inset the picture stands off the block edge by
    return { imageWidth: width, imageHeight: height, scale: Math.min(1, (containerWidth - 2 * MEDIA_INSET) / width) }
  }, [image, containerWidth])

  useEffect(() => {
    setSettled(!imageUrl) // no url → nothing to await, report ready immediately
    setFailed(false)
  }, [imageUrl])

  const heatmap = useMemo(() => getHeatmapData(answer, menuViewMode), [answer, menuViewMode])

  // the overlay's svg is React's and d3 only draws inside it, so the two never share a parent's children
  const showOverlay = settled && !failed && Boolean(display) && hasOverlayData({ menuViewMode, heatmap, sections })

  useEffect(() => {
    const svg = overlayRef.current
    if (!showOverlay || !svg) return

    drawOverlayInto(d3.select(svg), { mode: menuViewMode, heatmap, sections, scale: display.scale })

    return () => clearOverlay(svg)
  }, [showOverlay, display, heatmap, sections, menuViewMode])

  const handleExport = async (fileType, done) => {
    try {
      await onExport?.(fileType)
    } finally {
      done?.()
    }
  }

  if (!image?.original) return null

  const ready = settled && !failed && Boolean(display)

  return (
    <div className={styles.chart} ref={wrapRef}>
      {/* legacy drew the download icon inside the image onload — no button while loading or on error */}
      {ready && (
        <div className={styles.download}>
          <ExportDropdown options={EXPORT_OPTIONS} onExport={handleExport} />
        </div>
      )}

      {/* the one loading visual: the same skeleton the section shows pre-visibility, at the
          exact final image height — no jump when the pixels arrive (D10) */}
      {!settled && (
        <ChartPlaceholder
          height={calcMenuChartHeight(image, containerWidth - 2 * MEDIA_INSET)}
          isLoading
          isAnimated={isAnimated}
        />
      )}

      <div id={`mn_heatmap_${id}`} className={styles.heatmap}>
        {display && imageUrl && (
          <img
            src={imageUrl}
            alt=""
            onLoad={() => setSettled(true)}
            onError={() => {
              setFailed(true)
              setSettled(true)
            }}
            style={{
              width: Math.round(display.imageWidth * display.scale),
              height: Math.round(display.imageHeight * display.scale),
              display: ready ? 'block' : 'none'
            }}
          />
        )}

        {showOverlay && (
          <svg
            id={overlayId(id)}
            ref={overlayRef}
            className={OVERLAY_CLASS}
            {...overlaySize({ width: display.imageWidth, height: display.imageHeight, scale: display.scale })}
            style={{ position: 'absolute', top: 0, left: 0, opacity: overlayOpacity(menuViewMode) ?? undefined }}
          />
        )}
      </div>
    </div>
  )
}

MenuChart.displayName = 'MenuChart'

MenuChart.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  image: PropTypes.shape({
    original: PropTypes.shape({
      url: PropTypes.string,
      path: PropTypes.string,
      width: PropTypes.number,
      height: PropTypes.number
    })
  }),
  answer: answerPropType,
  sections: PropTypes.array,
  menuViewMode: PropTypes.oneOf(Object.values(MENU_VIEW_MODES)),
  isAnimated: PropTypes.bool,
  onExport: PropTypes.func
}

export default MenuChart
